// src/hooks/useTemplatePreviewScene.js
// Izole THREE.js sahnesi: baslik sablon editor preview icin
import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export function useTemplatePreviewScene(containerRef) {
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const animFrameRef = useRef(null);
  const isDisposedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    isDisposedRef.current = false;

    const width = container.clientWidth || 400;
    const height = container.clientHeight || 300;

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Orthographic Camera
    const camera = new THREE.OrthographicCamera(
      width / -2, width / 2, height / 2, height / -2, -1000, 1000
    );
    camera.position.set(0, 0, 10);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setClearColor(0x0a0a14, 1);
    renderer.sortObjects = true;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Controls - preview icin pan (zoom is custom)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableRotate = false;
    controls.enablePan = true;
    controls.enableZoom = false; // Custom zoom handler kullanılacak
    controls.mouseButtons = {
      LEFT: null,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: THREE.MOUSE.PAN,
    };
    controlsRef.current = controls;

    // AutoCAD tarzı mouse imlecine doğru zoom
    const zoomSpeed = 1.1;
    const handleWheel = (event) => {
      event.preventDefault();
      const rect = renderer.domElement.getBoundingClientRect();
      const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const beforeWorld = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera);
      if (event.deltaY < 0) {
        camera.zoom *= zoomSpeed;
      } else {
        camera.zoom /= zoomSpeed;
      }
      camera.zoom = Math.max(0.01, Math.min(camera.zoom, 1000));
      camera.updateProjectionMatrix();
      const afterWorld = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera);
      const delta = beforeWorld.clone().sub(afterWorld);
      camera.position.add(delta);
      controls.target.add(delta);
      camera.updateProjectionMatrix();
    };
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

    // Animasyon dongusu
    const animate = () => {
      if (isDisposedRef.current) return;
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize observer - debounce ile sonsuz donguyu onle
    let resizeTimeout = null;
    let lastW = width;
    let lastH = height;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) return; // Debounce: onceki henuz bitmedi
      resizeTimeout = setTimeout(() => {
        resizeTimeout = null;
        if (isDisposedRef.current) return;
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (w === 0 || h === 0) return;
        if (w === lastW && h === lastH) return; // Boyut degismedi, isleme yok
        lastW = w;
        lastH = h;
        // En/boy oranını koru: mevcut dikey dünya açıklığını sabit tut, yatayı aspect'e göre ayarla
        const currentHeight = camera.top - camera.bottom;
        const newWidth = currentHeight > 0 ? currentHeight * (w / h) : w;
        camera.left = -newWidth / 2;
        camera.right = newWidth / 2;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
      }, 100);
    });
    resizeObserver.observe(container);

    return () => {
      isDisposedRef.current = true;
      resizeObserver.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      sceneRef.current = null;
      cameraRef.current = null;
      rendererRef.current = null;
      controlsRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sahneyi temizle
  const clearScene = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const toRemove = [...scene.children]; // Kopya al, dongu sirasinda mutasyon onle
    toRemove.forEach(obj => {
      scene.remove(obj);
      obj.traverse?.((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => {
              if (m.map) m.map.dispose();
              m.dispose();
            });
          } else {
            if (child.material.map) child.material.map.dispose();
            child.material.dispose();
          }
        }
      });
    });
  }, []);

  // Kameraya sahneyi sigdir
  const fitToContent = useCallback(() => {
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const container = containerRef.current;
    if (!scene || !camera || !container || scene.children.length === 0) return;

    const box = new THREE.Box3().setFromObject(scene);
    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    if (size.x === 0 && size.y === 0) return;

    // Viewport en/boy oranına göre frustum ayarla — aksi halde içerik geriliyor
    const viewW = container.clientWidth || 1;
    const viewH = container.clientHeight || 1;
    const aspect = viewW / viewH;
    const contentAspect = size.y > 0 ? size.x / size.y : aspect;

    let worldW, worldH;
    if (contentAspect > aspect) {
      worldW = size.x * 1.2;
      worldH = worldW / aspect;
    } else {
      worldH = size.y * 1.2;
      worldW = worldH * aspect;
    }
    if (worldW === 0 || worldH === 0) return;

    camera.left = -worldW / 2;
    camera.right = worldW / 2;
    camera.top = worldH / 2;
    camera.bottom = -worldH / 2;
    camera.zoom = 1;
    camera.position.set(center.x, center.y, 10);
    camera.lookAt(center.x, center.y, 0);
    camera.updateProjectionMatrix();

    if (controlsRef.current) {
      controlsRef.current.target.set(center.x, center.y, 0);
      controlsRef.current.update();
    }
  }, [containerRef]);

  return {
    scene: sceneRef,
    camera: cameraRef,
    renderer: rendererRef,
    clearScene,
    fitToContent,
  };
}
